import { useQuery } from '@tanstack/react-query';
import { getRewards } from 'services/offerings';

/**
 * Single source of truth for offering rewards data across the
 * BuilderLayout. Consolidates what used to be three separate cache
 * entries:
 *
 *   - `['rewards']`              — global, no offeringId in key (used by
 *                                  BuilderClassicTwoColumnLayout,
 *                                  BuilderClassicInvestSidebar,
 *                                  BuilderGetEquityBubble,
 *                                  BuilderClassicSectionNav)
 *   - `['builder-hero-rewards', id]`  — BuilderHeroSection (Early Bird countdown)
 *   - `['builder-rewards', id]`       — BuilderPerksSection
 *
 * Three different queryKeys for the same underlying API call meant
 * `getRewards` was being hit ~3× per page load on Greenfield
 * (Jun 2026 perf audit). This hook funnels everyone through
 * `['rewards', offeringId]` so React Query dedupes them to a single
 * fetch. Including `offeringId` in the key (vs the old `['rewards']`
 * naked key) also fixes a latent bug where navigating between
 * offerings would briefly show the wrong offering's rewards from
 * cache.
 *
 * Pass `enabled: false` (or omit `offeringId`) to defer the fetch —
 * useful when the consumer wants to lazy-mount the dependent UI
 * (e.g. BuilderPerksSection gates this on `useInViewSticky`).
 */
const useGetRewards = ({
  offeringId,
  enabled = true,
}: {
  offeringId: string | undefined;
  enabled?: boolean;
}) => {
  const { data, isLoading, isFetching, isSuccess, isError, refetch } = useQuery({
    queryKey: ['rewards', offeringId],
    // `enabled: Boolean(offeringId)` below guarantees this only runs when
    // `offeringId` is a non-empty string, so the non-null assertion in
    // `getRewards({ id: offeringId! })` is safe — the underlying service
    // signature requires `string`. React Query's `enabled` gate is the
    // type-safe shape here, even though TypeScript can't prove the link.
    queryFn: () => getRewards({ id: offeringId as string }),
    enabled: Boolean(offeringId) && enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    refetch,
  };
};

export default useGetRewards;
